import { Create, Edit, SaveButton } from '@refinedev/antd';
import { useTranslate } from '@refinedev/core';
import {
    ButtonProps,
    Col,
    Drawer,
    DrawerProps,
    Form,
    FormProps,
    Grid,
    Input,
    InputNumber,
    Row,
    Select,
    UploadFile,
} from 'antd';
import { useState } from 'react';

import { ProductFrequencyEnum } from '../../enums/frequency.enum';
import { Hint } from '../modules/hint';
import { ImageUpload } from '../modules/image-upload';
import { Toggle } from '../modules/toggle';

type ProductDetailsProps = {
    drawerProps: DrawerProps;
    formProps: FormProps;
    saveButtonProps: ButtonProps;
    serviceId: string;
    isEditMode: boolean;
};
const { Option } = Select;

export const ProductDetails: React.FC<ProductDetailsProps> = ({
    drawerProps,
    formProps,
    saveButtonProps,
    serviceId,
    isEditMode,
}) => {
    const t = useTranslate();
    const [loading, setLoading] = useState<boolean>(false);
    const breakpoint = Grid.useBreakpoint();

    const initialValues: any =
        !isEditMode && !formProps.initialValues?.id
            ? { isActive: true }
            : {
                  ...formProps.initialValues,
              };

    const onChangeImage = (file: UploadFile<any>) => {
        formProps.form?.setFieldsValue({ featureImageUrl: file.response });
        if (file.status === 'uploading') {
            setLoading(true);
        } else {
            setLoading(false);
        }
    };

    const form = (
        <Form
            {...formProps}
            onFinish={(values) => {
                values.serviceId = serviceId;
                values.price = values.price * 1;
                values.featureImageUrl =
                    values.featureImageUrl?.length > 0 ? values.featureImageUrl : null;
                formProps.onFinish && formProps.onFinish(values);
            }}
            layout="vertical"
            initialValues={{ ...initialValues }}
        >
            <Row gutter={20}>
                <Col md={16}>
                    <Form.Item
                        label={t('services.fields.name')}
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: 'Name is required',
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label={t('services.fields.description')}
                        name="description"
                        rules={[
                            {
                                required: true,
                                message: 'Description is required',
                            },
                        ]}
                    >
                        <Input.TextArea rows={4} />
                    </Form.Item>

                    <Form.Item
                        label={t('services.fields.frequency')}
                        name="frequency"
                        rules={[
                            {
                                required: true,
                                message: 'Frequency is required',
                            },
                        ]}
                    >
                        <Select>
                            <Option value={ProductFrequencyEnum.ONE_TIME_PAYMENT}>
                                {t('services.frequency.' + ProductFrequencyEnum.ONE_TIME_PAYMENT)}
                            </Option>
                            <Option value={ProductFrequencyEnum.PER_MONTH}>
                                {t('services.frequency.' + ProductFrequencyEnum.PER_MONTH)}
                            </Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        label={t('services.fields.price')}
                        name="price"
                        rules={[
                            {
                                required: true,
                                message: 'Price is required',
                            },
                        ]}
                    >
                        <InputNumber prefix="$" />
                    </Form.Item>
                    <Form.Item
                        rules={[
                            {
                                required: true,
                                message: 'Status is required',
                            },
                        ]}
                        label={t('services.fields.isActive')}
                        name="isActive"
                    >
                        <Toggle />
                    </Form.Item>
                </Col>
                <Col md={8}>
                    <Form.Item
                        label={t('services.fields.images.label')}
                        name="featureImageUrl"
                        rules={[
                            {
                                required: true,
                                message: 'Image is required',
                            },
                        ]}
                    >
                        <ImageUpload
                            width={300}
                            height={200}
                            onChange={(fileOrn) => {
                                if (fileOrn) {
                                    onChangeImage(fileOrn);
                                }
                            }}
                            uploadText="Image"
                            value={initialValues?.featureImageUrl}
                        />
                        <Hint width={300} height={200} size={5} fileType={['JPG', 'PNG']} />
                    </Form.Item>
                </Col>
            </Row>
        </Form>
    );

    return drawerProps.open ? (
        <Drawer
            {...drawerProps}
            width={breakpoint.md ? '700px' : '100%'}
            maskClosable={false}
            zIndex={999}
            className="drawer-form-container service-drawer"
            afterOpenChange={() => {
                formProps.form?.resetFields();
            }}
            title={
                <span className="text-lg">{!isEditMode ? 'Create Product' : 'Edit Product'}</span>
            }
            extra={<SaveButton {...saveButtonProps} />}
        >
            {!isEditMode ? (
                <Create
                    title={<></>}
                    breadcrumb={null}
                    headerProps={{ backIcon: null }}
                    isLoading={!loading ? false : true}
                    footerButtons={<></>}
                >
                    {form}
                </Create>
            ) : (
                <Edit
                    title={<></>}
                    breadcrumb={null}
                    headerProps={{ backIcon: null, extra: null }}
                    isLoading={loading || !initialValues?.id ? true : false}
                    footerButtons={<></>}
                >
                    {form}
                </Edit>
            )}
        </Drawer>
    ) : (
        <></>
    );
};
